export const cssNotes = [
    {
      id: 401,
      topic: "css",
      seriesKey: "css",
      title: "CSS Basics",
      description:
        "Understanding what CSS is, its origin, and the three primary ways to link styles to an HTML document.",
      content: `
##  What is CSS?
**CSS** stands for **Cascading Style Sheets**.
* It is a style sheet language used to describe the presentation of a document written in a markup language like HTML.
* History: Developed by Håkon Wium Lie in 1996.
##  How CSS Works with HTML
CSS targets elements in the HTML structure (the DOM) using **selectors** and applies style rules (properties and values) to them.
##  Ways to Include CSS
### 1. External Styles (Best Practice)
* The most recommended method. Styles are kept in a separate \`.css\` file.
* Link: Use the \`<link>\` tag in the HTML \`<head>\`.
\`\`\`html
<link rel="stylesheet" href="/css/styles.css">
\`\`\`
### 2. Internal Styles
* Styles are placed within a \`<style>\` tag in the HTML \`<head>\`.
* Used when styling a single page uniquely.
### 3. Inline Styles
* Styles are applied directly to an individual HTML element using the \`style\` attribute.
* Avoid this: It mixes content and presentation, reduces readability, and has very high specificity.
\`\`\`html
<p style="color: red; font-size: 14px;">Inline style example.</p>
\`\`\`
##  CSS Syntax Basics
A CSS rule consists of a **Selector** and a **Declaration Block**.
\`\`\`css
/* Selector  */
p {
  /* Property : Value; */
  color: blue; /* Declaration */
  font-size: 16px; /* Declaration */
}
\`\`\`
      `,
      tags: ["CSS", "Basics", "Syntax", "Inclusion", "External", "Inline"],
      date: "2024-10-23",
    },
    {
      id: 402,
      topic: "css",
      seriesKey: "css",
      title: "CSS Selectors",
      description:
        "A complete guide to the different types of CSS selectors used to target and style specific HTML elements.",
      content: `
##  CSS Selectors
Selectors are patterns used to select the elements you want to style.
| Selector Type | Syntax | Description | Example |
| :--- | :--- | :--- | :--- |
| Universal | \`*\` | Selects all elements. | \`* { margin: 0; }\` |
| Type (Element) | \`element\` | Selects all elements of that type. | \`div { ... }\` |
| Class | \`.classname\` | Selects all elements with that class attribute. | \`.btn-primary { ... }\` |
| ID | \`#idname\` | Selects the single element with that ID attribute. | \`#header-logo { ... }\` |
| Grouping | \`sel1, sel2\` | Selects all elements in the comma-separated list. | \`h1, h2, p { ... }\` |
---
### Combinator Selectors
These combine two or more simple selectors based on their relationship:
* Descendant Selector: Targets an element that is anywhere inside another element.
    \`\`\`css
    /* Targets ALL <a> tags inside a <nav> */
    nav a { color: blue; } 
    \`\`\`
* Child Selector: Targets an element that is a direct child of another.
    \`\`\`css
    /* Targets only <li> that are direct children of a <ul> */
    ul > li { list-style: none; } 
    \`\`\`
### Attribute Selectors
Targets elements based on the presence or value of an attribute.
\`\`\`css
/* Targets all input elements with type="text" */
input[type="text"] { border: 1px solid gray; }
\`\`\`
      `,
      tags: ["CSS", "Selectors", "Class", "ID", "Descendant", "Attribute"],
      date: "2024-10-24",
    },
    {
      id: 403,
      topic: "css",
      seriesKey: "css",
      title: "Colors and Backgrounds",
      description:
        "Defining colors using various formats and applying complex background styles like images and repeat options.",
      content: `
##  Color Formats
CSS supports several ways to define colors for the **\`color\`** property (text color) and **\`background-color\`**.
| Format | Description | Example |
| :--- | :--- | :--- |
| Named Colors | Simple, predefined names. | \`color: red;\` |
| Hexadecimal | 6-digit hex code (\`#RRGGBB\`). | \`#336699\` |
| RGB/A | Red, Green, Blue components (0-255). A is Alpha (Opacity: 0.0 to 1.0). | \`rgb(51, 102, 153)\` / \`rgba(0, 0, 0, 0.5)\` |
| HSL/A | Hue, Saturation, Lightness. Intuitive for picking shades. | \`hsl(210, 50%, 40%)\` / \`hsla(180, 100%, 50%, 0.8)\` |
##  Background Properties
These control the background of any element.
**\`background-color\`**: Sets a solid color.
 **\`background-image\`**: Specifies one or more background images.
    \`\`\`css
    background-image: url('images/hero.jpg');
    \`\`\`
 **\`background-repeat\`**: Defines how the image is tiled. (\`repeat-x\`, \`repeat-y\`, **\`no-repeat\`**).
 **\`background-size\`**: Specifies the size of the background image.
    * \`cover\`: Scales the image to cover the entire container.
    * \`contain\`: Scales the image to fit entirely within the container.
 **\`background-position\`**: Sets the starting position of a background image (\`center\`, \`top left\`, \`50% 50%\`).
### Shorthand Property
You can combine most background properties into a single, compact declaration:
\`\`\`css
background: #eee url('image.png') no-repeat center / cover;
/* Color | Image | Repeat | Position | / Size */
\`\`\`
      `,
      tags: ["CSS", "Colors", "Background", "Hex", "RGB", "HSL"],
      date: "2024-10-24",
    },
    {
      id: 404,
      topic: "css",
      seriesKey: "css",
      title: "Text and Fonts",
      description:
        "Styling text appearance, choosing fonts, controlling line spacing, and integrating external font services like Google Fonts.",
      content: `
##  Font Properties
These control the appearance of the typeface.
| Property | Purpose | Example |
| :--- | :--- | :--- |
| \`font-family\` | The typeface to use. Always include fallback generic families. | \`'Roboto', Helvetica, sans-serif;\` |
| \`font-size\` | The size of the text. | \`16px\` or \`1.2rem;\` |
| \`font-weight\` | The thickness of the characters. | \`normal\`, \`bold\`, or numbers (100-900). |
| \`font-style\` | \`normal\`, \`italic\`, or \`oblique\`. | \`font-style: italic;\` |
| \`font-variant\` | Renders text in small capital letters. | \`font-variant: small-caps;\` |
### Google Fonts Integration
*  Link: Add the Google Fonts \`<link>\` tag to your HTML \`<head>\`.
*  Use: Apply the font-family in your CSS.
\`\`\`css
body {
  font-family: 'Open Sans', sans-serif;
}
\`\`\`
##  Text Properties
These control the layout and decoration of text blocks.
 **\`color\`**: Sets the color of the text (foreground color).
 **\`text-align\`**: Horizontal alignment (\`left\`, \`right\`, \`center\`, \`justify\`).
 **\`text-decoration\`**: Adds or removes decorations (\`none\`, \`underline\`, \`overline\`, \`line-through\`).
 **\`text-transform\`**: Changes case (\`uppercase\`, \`lowercase\`, \`capitalize\`).
 **\`line-height\`**: The height of a line of text (distance between baselines).
 **\`letter-spacing\`**: The space between characters.
      `,
      tags: [
        "CSS",
        "Typography",
        "Fonts",
        "Text",
        "font-family",
        "Google Fonts",
      ],
      date: "2024-10-25",
    },
    {
      id: 405,
      topic: "css",
      seriesKey: "css",
      title: "The Box Model and box-sizing",
      description:
        "Understanding the fundamental concept that every HTML element is a rectangular box composed of content, padding, border, and margin.",
      content: `
## The CSS Box Model
Every HTML element is treated as a rectangular box. The Box Model describes how its size and space around it are calculated.
From the inside out, the Box Model consists of four layers:
  **1.Content:** The actual content (text, image, video). Its size is defined by **\`width\`** and **\`height\`**.
  **2.Padding:** Space between the **content** and the **border**. It takes the background color of the element.
  **3.Border:** A line surrounding the padding and content.
  **4.Margin:** Space *outside* the **border**, separating the element from other elements. Margins are transparent.
\`\`\`css
.box {
  width: 200px;
  padding: 10px;    /* Adds 10px space inside the border */
  border: 5px solid black;
  margin: 20px;     /* Adds 20px space outside the border */
}
/* Total width = 200px (content) + 2*10px (padding) + 2*5px (border) = 230px */
\`\`\`
---
##  box-sizing Property
This property controls how the **\`width\`** and **\`height\`** properties are calculated.
### 1. \`content-box\` (Default)
* The \`width\` and \`height\` properties apply only to the content area.
* Padding and border are added to the specified width/height. (The example above uses this default calculation.)
### 2. \`border-box\` (Preferred)
* The \`width\` and \`height\` properties include the content, padding, and border.
* This makes sizing much more intuitive.
\`\`\`css
/* Recommended for all projects */
* {
  box-sizing: border-box;
}
.box {
  width: 200px; /* The element will be exactly 200px wide, including padding and border */
}
\`\`\`
##  Overflow
Controls what happens when content is too large to fit within an element's box (usually defined by fixed width/height).
 **\`visible\`** (Default): Content spills outside the box.
 **\`hidden\`**: Content is clipped and the rest is hidden.
 **\`scroll\`**: Scrollbars are always shown.
 **\`auto\`**: Scrollbars only appear if necessary.
      `,
      tags: [
        "CSS",
        "Box Model",
        "Padding",
        "Margin",
        "Border",
        "box-sizing",
        "Overflow",
      ],
      date: "2024-10-25",
    },
    {
      id: 406,
      topic: "css",
      seriesKey: "css",
      title: "Display, Visibility, and Opacity",
      description:
        "Controlling how an element is rendered in the flow of the document and how its visibility is managed.",
      content: `
##  The \`display\` Property
The \`display\` property is one of the most important in CSS, defining the element's block/inline nature and its layout method.
| Value | Behavior | 
| :--- | :--- | 
| \`block\` | Takes up the full available width; starts on a new line. Accepts width/height. (e.g., \`div\`, \`p\`) |
| \`inline\` | Takes up only the width necessary; does not start on a new line. Ignores setting width/height. (e.g., \`span\`, \`a\`) |
| \`inline-block\` | Flows like an inline element but respects width/height and vertical margins. |
| \`none\` | Completely removes the element from the document flow; it takes up **no space**. |
| \`flex\` | Turns the element into a Flex Container, enabling Flexbox layout. |
| \`grid\` | Turns the element into a Grid Container, enabling CSS Grid layout. |
---
##  Visibility Control
These properties control whether an element is viewable.
### 1. \`visibility\`
 **\`visibility: hidden\`**: Hides the element, but it still occupies its original space in the document flow.
 **\`visibility: visible\`** (Default): Displays the element.
### 2. \`opacity\`
* Sets the transparency level of an element (0.0 to 1.0).
* \`opacity: 0\` makes the element invisible, but it remains in the document flow and is still clickable.
      `,
      tags: [
        "CSS",
        "Display",
        "Inline",
        "Block",
        "Flex",
        "Grid",
        "Visibility",
        "Opacity",
      ],
      date: "2024-10-26",
    },
    {
      id: 407,
      topic: "css",
      seriesKey: "css",
      title: "Positioning Elements and z-index",
      description:
        "Mastering the five positioning schemes to control the exact location of an element on the page.",
      content: `
##  The \`position\` Property
The \`position\` property defines how an element is placed on the page, often combined with directional properties: **\`top\`**, **\`bottom\`**, **\`left\`**, and **\`right\`**.
| Value | Description |
| :--- | :--- |
| \`static\` | Default. The element follows the normal document flow. Directional properties (\`top/left\`) have no effect. |
| \`relative\` | Positioned relative to its *normal* position. Directional properties move it *from* that spot, but the original space is preserved. |
| \`absolute\` | Positioned relative to its nearest positioned ancestor (i.e., ancestor with position other than \`static\`). Removed from the normal flow. |
| \`fixed\` | Positioned relative to the **viewport** (browser window). It stays in place even when the user scrolls. |
| \`sticky\` | Positioned relative to its nearest scrolling ancestor. Behaves like \`relative\` until a threshold is met, then becomes \`fixed\`. |
### Absolute Positioning Tip
To position an element absolutely relative to its parent, the parent must have **\`position: relative;\`** (or \`absolute\`, \`fixed\`, \`sticky\`).
\`\`\`css
.parent {
  position: relative;
}
.child {
  position: absolute;
  top: 10px; /* 10px from the top edge of the parent */
  right: 10px;
}
\`\`\`
##  z-index
Controls the vertical stacking order (or depth) of elements that **overlap** and have a non-static position (\`relative\`, \`absolute\`, \`fixed\`, or \`sticky\`).
* Higher \`z-index\` values are closer to the user and appear on top of elements with lower values.
* It only works on positioned elements.
      `,
      tags: [
        "CSS",
        "Positioning",
        "relative",
        "absolute",
        "fixed",
        "sticky",
        "z-index",
      ],
      date: "2024-10-26",
    },
    {
      id: 408,
      topic: "css",
      seriesKey: "css",
      title: "Flexbox: One-Dimensional Layout Master",
      description:
        "A powerful tool for arranging, aligning, and distributing space among items in a container, primarily along a single axis (row or column).",
      content: `
##  Flexbox (Flexible Box Layout)
Flexbox is a one-dimensional layout model designed to distribute space among items in a single row or column.
### 1. The Container (Parent)
To start, set **\`display: flex\`** on the parent element.
| Property | Purpose | Values |
| :--- | :--- | :--- |
| \`flex-direction\` | Defines the main axis (row or column). | \`row\` (default), \`column\`, \`row-reverse\`, \`column-reverse\` |
| \`justify-content\` | Aligns items along the main axis. | \`flex-start\`, \`center\`, \`flex-end\`, \`space-between\`, \`space-around\` |
| \`align-items\` | Aligns items along the cross axis (perpendicular to the main axis). | \`stretch\`, \`flex-start\`, \`center\`, \`flex-end\`, \`baseline\` |
| \`flex-wrap\` | Allows items to wrap onto multiple lines if they exceed container width. | \`nowrap\` (default), \`wrap\`, \`wrap-reverse\` |
### 2. The Items (Children)
These properties control individual items within the flex container.
| Property | Purpose |
| :--- | :--- |
| \`flex-grow\` | Specifies how much an item will grow relative to the rest of the flex items (default is 0). |
| \`flex-shrink\` | Specifies how much an item will shrink relative to the rest (default is 1). |
| \`flex-basis\` | The initial size of the flex item before space is distributed. |
| \`order\` | Specifies the order of the item (default is 0). |
| \`align-self\` | Overrides the container's \`align-items\` property for this specific item. |
### Common Use Case: Centering
The easiest way to horizontally and vertically center an item:
\`\`\`css
.container {
  display: flex;
  justify-content: center; /* Main axis (horizontal) */
  align-items: center;    /* Cross axis (vertical) */
  height: 100vh;
}
\`\`\`
      `,
      tags: [
        "CSS",
        "Flexbox",
        "Layout",
        "Container",
        "flex-direction",
        "justify-content",
        "align-items",
      ],
      date: "2024-10-27",
    },
    {
      id: 409,
      topic: "css",
      seriesKey: "css",
      title: "CSS Grid: Two-Dimensional Layout Master",
      description:
        "Learning CSS Grid, the most powerful layout system for creating complex, two-dimensional structures (rows and columns).",
      content: `
##  CSS Grid Layout
CSS Grid is a two-dimensional layout system that allows you to define rows and columns simultaneously. Ideal for overall page structures and complex interfaces.
### 1. The Container (Parent)
To start, set **\`display: grid\`** on the parent element.
| Property | Purpose |
| :--- | :--- |
| \`grid-template-columns\` | Defines the number and width of columns. |
| \`grid-template-rows\` | Defines the number and height of rows. |
| \`gap\` | Sets the spacing (gutters) between columns and rows. Shorthand for \`grid-row-gap\` and \`grid-column-gap\`. |
| \`grid-auto-flow\`| Controls how auto-placed items are flowed into the grid (\`row\`, \`column\`, \`dense\`). |
### Example: Defining a 3-Column Grid
The \`fr\` unit (fractional unit) is unique to Grid and represents a fraction of the available space.
\`\`\`css
.container {
  display: grid;
  /* 3 columns: 1 fractional unit, 100px fixed, 2 fractional units */
  grid-template-columns: 1fr 100px 2fr;
  gap: 20px;
}
\`\`\`
### 2. The Items (Children)
These properties control where an individual item is placed on the grid.
| Property | Purpose |
| :--- | :--- |
| \`grid-column\` | Defines where an item starts and ends across the columns. |
| \`grid-row\` | Defines where an item starts and ends across the rows. |
### Placement Example
\`\`\`css
.item-a {
  /* Starts at line 1, spans 3 columns (ends at line 4) */
  grid-column: 1 / span 3;
  /* Starts at line 2, spans 2 rows (ends at line 4) */
  grid-row: 2 / 4; 
}
\`\`\`
      `,
      tags: [
        "CSS",
        "Grid",
        "Layout",
        "2D",
        "grid-template-columns",
        "gap",
        "fr",
      ],
      date: "2024-10-27",
    },
    {
      id: 410,
      topic: "css",
      seriesKey: "css",
      title: "Transitions, Animations, and Transformations",
      description:
        "Adding motion and interactivity to elements using smooth property changes (transitions) and complex, predefined motion sequences (@keyframes).",
      content: `
##  CSS Transitions
CSS Transitions provide a way to control the speed of changes from one CSS property value to another. This creates smooth, non-instantaneous motion.
| Property | Purpose |
| :--- | :--- |
| \`transition-property\` | The property to animate (\`color\`, \`opacity\`, \`all\`). |
| \`transition-duration\` | How long the transition takes (\`0.3s\`, \`300ms\`). |
| \`transition-timing-function\` | The acceleration curve (\`ease\`, \`linear\`, \`ease-in-out\`). |
| \`transition-delay\` | Wait time before the transition starts. |
\`\`\`css
.button {
  background-color: blue;
  transition: background-color 0.4s ease-in-out; /* Shorthand */
}
.button:hover {
  background-color: darkblue; /* The transition occurs when hover is applied */
}
\`\`\`
---
##  CSS Animations (\`@keyframes\`)
Animations allow for multi-step, complex motion effects.
1.  **Define the Animation:** Use **\`@keyframes\`** to define the sequence of styles at specific points (\`from\`/\`to\` or \`0% \`/\`100%\`).
2.  **Apply to Element:** Use animation properties to link the element to the keyframes.
| Property | Purpose |
| :--- | :--- |
| \`animation-name\` | Links to the \`@keyframes\` rule. |
| \`animation-duration\` | Total time of one cycle. |
| \`animation-iteration-count\` | How many times to repeat (\`1\`, \`infinite\`). |
##  Transformations (\`transform\`)
Modifies the visual appearance of an element without affecting its layout.
 **\`translate(x, y)\`**: Moves the element (translation).
 **\`rotate(angle)\`**: Rotates the element around its center.
 **\`scale(x, y)\`**: Resizes the element.
 **\`skew(x-angle, y-angle)\`**: Skews the element.
      `,
      tags: [
        "CSS",
        "Transitions",
        "Animations",
        "Keyframes",
        "Transform",
        "Motion",
      ],
      date: "2024-10-28",
    },
    {
      id: 411,
      topic: "css",
      seriesKey: "css",
      title: "Pseudo-classes and Pseudo-elements",
      description:
        "Using selectors that target elements based on their state (:pseudo-class) or target a specific part of an element (::pseudo-element).",
      content: `
##  Pseudo-classes (:)
Selects an element based on its **state, position, or property**, not its content or attributes.
| Pseudo-class | Purpose | Example |
| :--- | :--- | :--- |
| \`a:hover\` | Element is hovered over by the mouse. | \`a:hover { color: red; }\` |
| \`a:focus\` | Element has input focus (e.g., a form field). | \`input:focus { border-color: blue; }\` |
| \`a:active\` | Element is being clicked or activated. | |
| \`p:first-child\` | Selects an element that is the first child of its parent. | \`p:first-child { font-weight: bold; }\` |
| \`p:nth-child(n)\` | Selects elements based on a formula (e.g., \`odd\`, \`even\`, or \`3n\`). | \`li:nth-child(2n) { background: lightgray; }\` |
| \`input:required\` | Selects form elements with the \`required\` attribute. | |
## Pseudo-elements (::)
Used to style a specific, logical part of an element that is **not** explicitly defined in the HTML.
| Pseudo-element | Purpose |
| :--- | :--- |
| \`::before\` | Inserts generated content before an element's content. Requires the \`content\` property. |
| \`::after\` | Inserts generated content after an element's content. Requires the \`content\` property. |
| \`::first-letter\` | Selects the first letter of a block-level element. |
| \`::first-line\` | Selects the first line of a block-level element. |
### \`::before\` / \`::after\` Example (Decoration)
\`\`\`css
.icon::before {
  content: "★ "; /* Inserts a star character */
  color: gold;
}
\`\`\`
      `,
      tags: [
        "CSS",
        "Selectors",
        "Pseudo-classes",
        "Pseudo-elements",
        "hover",
        "nth-child",
        "before",
        "after",
      ],
      date: "2024-10-28",
    },
    {
      id: 412,
      topic: "css",
      seriesKey: "css",
      title: " Absolute vs. Relative Units",
      description:
        "Distinguishing between fixed and flexible CSS units, and leveraging relative units for responsive design.",
      content: `
##  Units and Measurements
CSS uses various units for defining lengths, sizes, and distances.
### 1. Absolute Length Units
These units are fixed and do not change size based on the viewport or parent element.
* \`px\` (Pixels): The most common unit. Represents a single dot on the screen. Absolute, but still rendered differently on high-density screens.
### 2. Relative Length Units (Recommended for RWD)
These units are scalable and change size relative to something else (e.g., viewport size, parent element's size, or root font size).
| Unit | Relative To | Purpose |
| :--- | :--- | :--- |
| \`%\` | Parent element's size. | Layout sizing (e.g., \`width: 50%;\`). |
| \`em\` | Parent element's font-size. | Scales relative to the container, which can cause cascading issues if nested. |
| \`rem\` | The root element's (\`<html>\`) font-size. | Preferred for typography scale, as it maintains consistency across the entire page. |
| \`vw\` | Viewport Width (1vw = 1% of viewport width). | Excellent for sizing elements or text that must scale with the browser window. |
| \`vh\` | Viewport Height (1vh = 1% of viewport height). | Used to define full-screen sections (e.g., \`height: 100vh;\`). |
### Responsive Unit Example
If the root font size is set to 16px:
* \`font-size: 1rem\` = 16px.
* \`font-size: 2rem\` = 32px.
\`\`\`css
/* Use rem for text sizing */
h1 {
  font-size: 3rem; /* Scales relative to the <html> font size */
}
/* Use vw for responsive width */
.hero-image {
  width: 80vw; /* Takes up 80% of the browser width */
}
\`\`\`
      `,
      tags: ["CSS", "Units", "rem", "em", "vh", "vw", "px", "Responsive"],
      date: "2024-10-29",
    },
    {
      id: 413,
      topic: "css",
      seriesKey: "css",
      title: "Responsive Design and Media Queries",
      description:
        "Creating web pages that adapt gracefully to different screen sizes and devices using the @media rule and breakpoints.",
      content: `
##  Responsive Design (RWD)
RWD is the practice of building a website suitable for any device, from small phones to large desktops.
### 1. Media Queries (\`@media\`)
Media Queries are the core of RWD. They allow you to apply CSS rules only when certain conditions are met (e.g., screen width, height, orientation).
\`\`\`css
/* Style applied to all devices */
.container {
  padding: 10px;
}

/* Styles applied only when the screen width is 768px or wider */
@media (min-width: 768px) {
  .container {
    padding: 20px; /* Increase padding on tablets and desktops */
    width: 80%;
  }
}
\`\`\`
### 2. Breakpoints
Breakpoints are the specific widths at which a website's layout needs to adjust.
* Common Breakpoints: 480px (small phones), 768px (tablets), 1024px (laptops), 1200px+ (desktops).
### 3. Mobile-First Approach (Recommended)
This approach prioritizes mobile devices first:
1.  Write base CSS for the smallest screen (mobile).
2.  Use \`min-width\` queries to progressively enhance the layout for larger screens.
3.Benefit: Ensures fast load times and a clean, foundational layout for the most resource-constrained devices.
### 4. Responsive Images
* Use the \`max-width: 100%\` CSS rule on images to prevent them from stretching beyond their container and breaking the layout.
      `,
      tags: ["CSS", "RWD", "Media Queries", "Breakpoints", "Mobile-First"],
      date: "2024-10-29",
    },
    {
      id: 414,
      topic: "css",
      seriesKey: "css",
      title: "CSS Variables",
      description:
        "Using custom properties to define reusable values globally or locally, enhancing consistency and making maintenance easier.",
      content: `
##  CSS Variables (Custom Properties)
CSS variables allow you to store a value in one place and reuse it throughout your stylesheets. They simplify design consistency and make bulk changes trivial.
### 1. Declaring Variables
Variables are declared using a double hyphen prefix *\`--\`*. They are case-sensitive.
* Global Scope: Declared on the \`:root\` pseudo-class to be available everywhere.
* Local Scope: Declared on any specific selector (e.g., \`.component\`) to be scoped to that element and its children.
\`\`\`css
/* Global Scope */
:root {
  --primary-color: #007bff;
  --spacing-md: 20px;
}

/* Local Scope */
.card {
  --card-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  box-shadow: var(--card-shadow);
}
\`\`\`
### 2. Using Variables (\`var()\`)
You retrieve the value using the **\`var()\`** function.
\`\`\`css
header {
  background-color: var(--primary-color); 
  padding: var(--spacing-md);
}

.button {
  color: var(--primary-color);
}
\`\`\`
### 3. Fallback Value
You can specify a fallback value inside the \`var()\` function in case the variable is not found.
\`\`\`css
color: var(--non-existent-color, black); /* Will use black if variable not found */
\`\`\`
### Benefits
* Consistency: Ensures brand colors, spacing, and font sizes are identical everywhere.
* Maintenance: Change one variable definition, and the entire site updates instantly.
      `,
      tags: [
        "CSS",
        "Variables",
        "Custom Properties",
        "var",
        "root",
        "Consistency",
      ],
      date: "2024-10-30",
    },
    {
      id: 415,
      topic: "css",
      seriesKey: "css",
      title: "Specificity, Inheritance, and the Cascade",
      description:
        "Understanding the three core concepts that determine which style rule wins when multiple rules target the same element.",
      content: `
## The Cascade
The "C" in CSS stands for **Cascading**, meaning styles flow from multiple sources and rules are combined according to a specific order:
1.  **Browser Defaults** (User Agent Styles)
2.  **User Styles** (Rare)
3.  **Author Styles** (Your CSS)
4.  **Important!** Rules marked with \`!important\` are the highest priority.
##  Specificity
Specificity is the algorithm that determines which CSS rule declaration is the most relevant and therefore wins the conflict. It's calculated based on the selector type.
### Specificity Hierarchy (From Highest to Lowest):
1.  **Inline Styles:** (1, 0, 0, 0 points) Always win unless \`!important\` is used elsewhere.
2.  **ID Selectors:** (0, 1, 0, 0 points)
3.  **Class, Attribute, Pseudo-classes:** (0, 0, 1, 0 points)
4.  **Type (Element) and Pseudo-elements:** (0, 0, 0, 1 points)
* Tie Breaker: If two selectors have the same specificity, the one declared **last** in the stylesheet wins.
### \`!important\` (Avoid Use)
Adding \`!important\` after a property value overrides **any** other declaration, regardless of specificity. It breaks the natural cascade flow and should be avoided except for rare utility classes or browser overrides.
\`\`\`css
.high-specificity-selector {
  color: red; /* Loses to the !important rule */
}
#unique-id {
  color: blue !important; /* Forces this color to be applied */
}
\`\`\`
##  Inheritance
Some CSS properties (like **\`color\`**, **\`font-family\`**, **\`font-size\`**, **\`line-height\`**) are **inherited** by child elements from their parent element. Layout properties (like \`margin\`, \`padding\`, \`border\`, \`width\`) are **not** inherited.
      `,
      tags: ["CSS", "Specificity", "Inheritance", "Cascade", "important"],
      date: "2024-10-30",
    },
    {
      id: 416,
      topic: "css",
      seriesKey: "css",
      title: "CSS Best Practices",
      description:
        "Guidelines for writing scalable, maintainable, and modern CSS code.",
      content: `
##  CSS Best Practices
### 1. Embrace Semantic Naming
* Use meaningful class names: Names should describe the purpose of the element, not its appearance (e.g., use \`.user-avatar\` or \`.error-message\` instead of \`.red-square\` or \`.float-left\`).
* Methodologies (BEM): Consider organizing classes using systems like BEM (Block, Element, Modifier) to make selectors clear and maintainable (\`.card__title--large\`).
### 2. Separation of Concerns
* Avoid Inline Styles: As discussed in 401, keep styles external.
* Avoid ID Selectors in CSS: IDs have high specificity, making them hard to override and reducing code flexibility. Reserve IDs for JavaScript interaction or anchor links.
### 3. Modern Layout Over Legacy
 ** Use Flexbox/Grid for Layouts:** These are specifically designed for layout tasks and are far superior to older techniques like **floats** or inline-block tricks.
 ** Avoid Magic Numbers:** Don't rely on arbitrary pixel values that break easily. Use relative units (\`rem\`, \`em\`, \`vh\`, \`vw\`) and CSS Variables for predictable scaling.
### 4. Code Organization and Readability
 ** Consistency:** Maintain a consistent order for declarations (e.g., layout, box model, typography, color, animation).
 ** Grouping:** Group related styles (e.g., all button styles, all form styles).
 ** Comments:** Use comments to explain non-obvious code sections, vendor prefixes (if necessary), and organization breaks.
      `,
      tags: [
        "CSS",
        "Best Practices",
        "Organization",
        "Flexbox",
        "BEM",
        "Maintainability",
      ],
      date: "2024-10-31",
    },
];
